/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useEffect, useMemo, useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { createRoot, type Root } from 'react-dom/client';

import { getDocument } from '@atlaskit/browser-apis';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { NodeView } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';
import { getKnowledgeSourceIcon } from '@atlassian/conversation-assistant-utils/knowledge-source-icons';

import { CONNECTOR_SOURCE_CHIP_NODE_NAME } from './connector-source-chip-node';

const ICON_SIZE = 12;

const iconSlotExpand = keyframes({
	from: {
		width: 0,
	},
	to: {
		width: ICON_SIZE,
	},
});

const chipStyles = css({
	display: 'inline',
	padding: '2px 0.5ch',
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	borderStyle: 'none',
	WebkitBoxDecorationBreak: 'clone',
	boxDecorationBreak: 'clone',
	color: token('color.text'),
	font: token('font.code'),
	overflow: 'auto',
	overflowWrap: 'break-word',
	whiteSpace: 'pre-wrap',
});

const innerStyles = css({
	display: 'inline-flex',
	alignItems: 'baseline',
	gap: token('space.025'),
});

const iconSlotStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: ICON_SIZE,
	height: ICON_SIZE,
	flexShrink: 0,
	verticalAlign: 'middle',
	overflow: 'hidden',
	transform: 'translateY(2px)', // visually center the icon with the text
	animationName: iconSlotExpand,
	animationDuration: token('motion.duration.xshort', '120ms'),
	animationTimingFunction: token('motion.easing.out.practical', 'ease-out'),
	animationFillMode: 'backwards',
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

const iconBoxStyles = css({
	display: 'inline-flex',
});

const skeletonStyles = css({
	display: 'block',
	width: ICON_SIZE,
	height: ICON_SIZE,
	borderRadius: token('radius.xsmall'),
	backgroundColor: token('color.skeleton'),
});

const remoteIconStyles = css({
	display: 'block',
	width: ICON_SIZE,
	height: ICON_SIZE,
	objectFit: 'contain',
});

const hiddenStyles = css({
	display: 'none',
});

const labelStyles = css({
	font: 'inherit',
});

const getStringAttr = (value: unknown): string => (typeof value === 'string' ? value : '');

const getChipAttrs = (node: PMNode): ConnectorSourceChipProps => ({
	source: getStringAttr(node.attrs.source),
	display: getStringAttr(node.attrs.display),
	iconUrl: getStringAttr(node.attrs.iconUrl) || undefined,
});

type ConnectorSourceChipIconProps = {
	source: string;
	iconUrl?: string;
};

const ConnectorSourceChipIcon = memo(({ source, iconUrl }: ConnectorSourceChipIconProps) => {
	const [isRemoteIconLoading, setIsRemoteIconLoading] = useState(Boolean(iconUrl));
	const [hasRemoteIconError, setHasRemoteIconError] = useState(false);

	useEffect(() => {
		setIsRemoteIconLoading(Boolean(iconUrl));
		setHasRemoteIconError(false);
	}, [iconUrl, source]);

	const localIcon = useMemo(() => getKnowledgeSourceIcon(source, { size: ICON_SIZE }), [source]);
	const fallbackIcon = useMemo(() => getKnowledgeSourceIcon('app', { size: ICON_SIZE }), []);
	const showRemoteIcon = !localIcon && iconUrl && !hasRemoteIconError;

	let content: JSX.Element | null;
	if (localIcon) {
		content = localIcon;
	} else if (showRemoteIcon) {
		content = (
			<span>
				{isRemoteIconLoading && <span css={skeletonStyles} />}
				<img
					css={[remoteIconStyles, isRemoteIconLoading ? hiddenStyles : undefined]}
					src={iconUrl}
					alt=""
					width={ICON_SIZE}
					height={ICON_SIZE}
					referrerPolicy="no-referrer"
					onLoad={() => setIsRemoteIconLoading(false)}
					onError={() => {
						setIsRemoteIconLoading(false);
						setHasRemoteIconError(true);
					}}
				/>
			</span>
		);
	} else {
		content = fallbackIcon;
	}

	return <span css={iconBoxStyles}>{content}</span>;
});
ConnectorSourceChipIcon.displayName = 'ConnectorSourceChipIcon';

type ConnectorSourceChipProps = {
	source: string;
	display: string;
	iconUrl?: string;
};

const areChipAttrsEqual = (
	previous: ConnectorSourceChipProps,
	next: ConnectorSourceChipProps,
): boolean =>
	previous.source === next.source &&
	previous.display === next.display &&
	previous.iconUrl === next.iconUrl;

const ConnectorSourceChip = memo(({ source, display, iconUrl }: ConnectorSourceChipProps) => {
	return (
		<span
			css={chipStyles}
			data-testid="connector-source-chip"
			data-connector-source-chip="true"
			data-source={source}
			data-display={display}
			{...(iconUrl ? { 'data-icon-url': iconUrl } : {})}
		>
			<span css={innerStyles}>
				<span css={iconSlotStyles} aria-hidden="true">
					<ConnectorSourceChipIcon source={source} iconUrl={iconUrl} />
				</span>
				<span css={labelStyles}>{display}</span>
			</span>
		</span>
	);
});
ConnectorSourceChip.displayName = 'ConnectorSourceChip';

const renderChip = (root: Root, attrs: ConnectorSourceChipProps): void => {
	const { source, display, iconUrl } = attrs;
	root.render(<ConnectorSourceChip source={source} display={display} iconUrl={iconUrl} />);
};

export const createConnectorSourceChipNodeView = (node: PMNode): NodeView => {
	const doc = getDocument();
	if (!doc) {
		throw new Error('Connector source chip node view requires a document');
	}

	let currentAttrs = getChipAttrs(node);
	const dom = doc.createElement('span');
	// Prevent the browser's native cursor from entering the chip's DOM boundary.
	// Without this, pressing ArrowRight or ArrowLeft briefly places the cursor
	// inside the chip where it is not visible, causing it to disappear until
	// the next move.
	dom.setAttribute('contenteditable', 'false');

	const root = createRoot(dom);

	renderChip(root, currentAttrs);

	return {
		dom,
		update(nextNode: PMNode): boolean {
			if (nextNode.type.name !== CONNECTOR_SOURCE_CHIP_NODE_NAME) {
				return false;
			}

			const nextAttrs = getChipAttrs(nextNode);
			if (areChipAttrsEqual(currentAttrs, nextAttrs)) {
				return true;
			}

			currentAttrs = nextAttrs;
			renderChip(root, currentAttrs);
			return true;
		},
		destroy(): void {
			queueMicrotask(() => root.unmount());
		},
	};
};
